import { useMemo } from 'react';

type DefectLegend = {
  id: number;
  name: string;
  groupKey: string;
  groupName: string;
  color?: string;
};

export const useTemplateFieldNames = (
  defects: DefectLegend[] | undefined,
  selectedConditionType?: string
) => {
  return useMemo(() => {
    if (!selectedConditionType) {
      return defects?.map(({ name }) => name.toLowerCase()) || [];
    }

    return (
      defects
        ?.filter(({ groupName }) => groupName === selectedConditionType)
        .map(({ name }) => name.toLowerCase()) || []
    );
  }, [defects, selectedConditionType]);
};

export const useGroupedDefects = (defects: DefectLegend[] | undefined) => {
  return useMemo(() => {
    return defects?.reduce((accumulator: { [key: string]: DefectLegend[] }, current) => {
      if (!accumulator[current.groupName]) {
        accumulator[current.groupName] = [];
      }
      accumulator[current.groupName].push(current);
      return accumulator;
    }, {});
  }, [defects]);
};

export const useActiveCoatingConditionLegends = (
  defects: DefectLegend[] | undefined,
  templateFieldNames: string[] | undefined
) => {
  return useMemo(() => {
    const activeLegends: { [key: string]: string } = {};

    defects?.forEach((defect) => {
      const name = defect.name;
      if (templateFieldNames?.includes(defect.name.toLowerCase())) {
        activeLegends[name.toLowerCase()] = defect?.color ?? '#000000';
      }
    });

    return activeLegends;
  }, [templateFieldNames, defects]);
};

export const useAllCoatingConditions = (
  defectLegend: DefectLegend[] | undefined,
  selectedConditionType: string,
  templateFieldNames: string[] | undefined,
  setSelectedCoatingCondition: React.Dispatch<React.SetStateAction<string[]>>
) => {
  return useMemo(() => {
    const allTemplates = defectLegend ?? [];
    if (templateFieldNames) setSelectedCoatingCondition(templateFieldNames);
    return allTemplates?.length > 0
      ? defectLegend?.filter(({ groupName }) => selectedConditionType === groupName)
      : [];
  }, [defectLegend, selectedConditionType, templateFieldNames, setSelectedCoatingCondition]);
};
